import {
  DashboardFilterBuilderIslandProps,
  DashboardFilterReaderIslandRenderProps,
} from './DashboardFilterIsland';
import {
  APIDashboardFilterExpression,
  SupportedDashboardFilterType,
} from '@ardoq/api-types';
import { Locale } from '@ardoq/locale';
import { isEmpty, noop } from 'lodash';
import { confirmDelete, ModalSize } from '@ardoq/modal';
import { type I18nLocale, t } from '@ardoq/i18n';
import {
  ContextDashboardIsUsedIn,
  DashboardFilter,
  DashboardFiltersCommands,
  UnlinkedDashboardFilter,
} from '../types';
import {
  getDashboardFilterPillProps,
  getUnlinkedDashboardFilterPillProps,
} from './filterPillPropsMapper';
import {
  getValidationErrorMessage,
  validateDashboardFilterName,
} from './validateDashboardFilterName';
import { dashboardFilterOperations } from './dashboardFilterOperations';
import {
  DashboardFilterPillsUIStateWithSetters,
  DashboardPillLabels,
  getInitialDashboardFilterPillUIState,
} from '@ardoq/table-filter-bar';

type GetDashboardFilterReaderIslandProps = {
  contextDashboardIsUsedIn: ContextDashboardIsUsedIn;
  dashboardFilters: Array<UnlinkedDashboardFilter | DashboardFilter>;
  clearAllSelectedValues: VoidFunction;
  locale: Locale;
  i18nLocale: I18nLocale;
  updateDashboardFilterValues: (
    dashboardFilterId: string,
    type: SupportedDashboardFilterType,
    values: APIDashboardFilterExpression[] | null
  ) => void;
  requestDashboardFilterSuggestions: (
    parameterId: string,
    query: string
  ) => void;
  requestDashboardFilterHierarchicalSuggestions: (
    parameterId: string,
    query: string
  ) => void;
  filterCachedHierarchicalSuggestions: (
    parameterId: string,
    query: string
  ) => void;
  isLoading: boolean;
  filterPillsUIStateWithSetters: Record<
    string,
    DashboardFilterPillsUIStateWithSetters
  >;
  dashboardPillLabels?: DashboardPillLabels;
};

export const getDashboardFilterReaderIslandProps = ({
  dashboardFilters,
  updateDashboardFilterValues,
  requestDashboardFilterSuggestions,
  requestDashboardFilterHierarchicalSuggestions,
  filterCachedHierarchicalSuggestions,
  contextDashboardIsUsedIn,
  clearAllSelectedValues,
  isLoading,
  locale,
  i18nLocale,
  filterPillsUIStateWithSetters,
  dashboardPillLabels,
}: GetDashboardFilterReaderIslandProps): DashboardFilterReaderIslandRenderProps => {
  return isEmpty(dashboardFilters) ||
    !dashboardFilters.some(dashboardFilterOperations.isLinkedDashboardFilter) ||
    contextDashboardIsUsedIn === 'usage-metrics'
    ? { show: false }
    : {
        show: true,
        showClearAllButton:
          dashboardFilters.length > 1 &&
          dashboardFilters.some(dashboardFilterOperations.hasValue),
        clearAllSelectedValues,
        dashboardFilterProps: dashboardFilters
          .filter(dashboardFilterOperations.isLinkedDashboardFilter)
          .map(dashboardFilter =>
            getDashboardFilterPillProps({
              dashboardFilter,
              updateDashboardFilterValues,
              requestDashboardFilterSuggestions,
              requestDashboardFilterHierarchicalSuggestions,
              filterCachedHierarchicalSuggestions,
              locale,
              i18nLocale,
              isLoading,
              isInReadOnlyMode: true,
              hasInaccessibleWidgets: false,
              filterPillUIState:
                filterPillsUIStateWithSetters[dashboardFilter.id],
              dashboardPillLabels,
            })
          ),
      };
};

type GetReadOnlyDashboardFilterIslandProps = {
  dashboardFilters: Array<UnlinkedDashboardFilter | DashboardFilter>;
  locale: Locale;
  i18nLocale: I18nLocale;
  dashboardPillLabels?: DashboardPillLabels;
};

// Non-interactive filter island for previews (e.g. Insights home settings). The
// caller is expected to wrap the rendered island in `inert` so keyboard and AT
// users can't reach the pill controls.
export const getReadOnlyDashboardFilterIslandProps = ({
  dashboardFilters,
  locale,
  i18nLocale,
  dashboardPillLabels,
}: GetReadOnlyDashboardFilterIslandProps): DashboardFilterReaderIslandRenderProps => {
  if (
    isEmpty(dashboardFilters) ||
    !dashboardFilters.some(dashboardFilterOperations.isLinkedDashboardFilter)
  ) {
    return { show: false };
  }
  return {
    show: true,
    showClearAllButton: false,
    clearAllSelectedValues: noop,
    dashboardFilterProps: dashboardFilters
      .filter(dashboardFilterOperations.isLinkedDashboardFilter)
      .map(dashboardFilter =>
        getDashboardFilterPillProps({
          dashboardFilter,
          updateDashboardFilterValues: noop,
          requestDashboardFilterSuggestions: noop,
          requestDashboardFilterHierarchicalSuggestions: noop,
          filterCachedHierarchicalSuggestions: noop,
          locale,
          i18nLocale,
          isLoading: false,
          isInReadOnlyMode: true,
          hasInaccessibleWidgets: false,
          filterPillUIState: {
            ...getInitialDashboardFilterPillUIState(),
            toggleIsOperatorPopoverOpen: noop,
            toggleIsValuePopoverOpen: noop,
            toggleShowDatePicker: noop,
            setPopoverInputValue: noop,
          },
          dashboardPillLabels,
        })
      ),
  };
};

type GetDashboardFilterBuilderIslandProps = {
  locale: Locale;
  i18nLocale: I18nLocale;
  isLoading: boolean;
  hasInaccessibleWidgets: boolean;
  showLinkChartPopover: boolean;
  commands: DashboardFiltersCommands;
  dashboardFilters: Array<UnlinkedDashboardFilter | DashboardFilter>;
  filterPillUI$State: Record<string, DashboardFilterPillsUIStateWithSetters>;
  dashboardPillLabels?: DashboardPillLabels;
};

export const getDashboardFilterBuilderIslandProps = ({
  dashboardFilters,
  showLinkChartPopover,
  hasInaccessibleWidgets,
  locale,
  i18nLocale,
  isLoading,
  commands,
  filterPillUI$State,
  dashboardPillLabels,
}: GetDashboardFilterBuilderIslandProps): DashboardFilterBuilderIslandProps => ({
  createDashboardFilterParameter: commands.createDashboardFilterParameter,
  clearAllSelectedValues: commands.clearAllSelectedValues,
  reorderDashboardFilterParameters: commands.reorderDashboardFilterParameters,
  showClearAllButton:
    dashboardFilters.length > 1 &&
    dashboardFilters.some(dashboardFilterOperations.hasValue),
  dashboardFilterNames: dashboardFilters.map(({ name }) => name),
  hasInaccessibleWidgetsForDashboardFilter: hasInaccessibleWidgets,
  dashboardFilterProps: dashboardFilters.map(dashboardFilter => {
    const settingsButtonProps = {
      removeFilter: dashboardFilterOperations.isUnlinkedDashboardFilter(
        dashboardFilter
      )
        ? () =>
            commands.removeDashboardFilterParameter(
              dashboardFilter.id,
              dashboardFilter.type
            )
        : () => {
            const linkedCount = dashboardFilter.linkCounts.total;
            confirmDelete({
              title: t(
                i18nLocale,
                'dashboard:filter.deleteConfirm.title',
                { name: dashboardFilter.name },
                'Delete {{name}}?'
              ),
              text: t(
                i18nLocale,
                linkedCount === 1
                  ? 'dashboard:filter.deleteConfirm.message_one'
                  : 'dashboard:filter.deleteConfirm.message_other',
                { count: linkedCount },
                linkedCount === 1
                  ? `This will remove {{count}} associated chart link and can't be undone. If filtered charts are used in a presentation, they will become unavailable.`
                  : `This will remove {{count}} associated chart links and can't be undone. If filtered charts are used in a presentation, they will become unavailable.`
              ),
              onConfirmAsync: async () => {
                commands.removeDashboardFilterParameter(
                  dashboardFilter.id,
                  dashboardFilter.type
                );
              },
              confirmButtonTitle: t(
                i18nLocale,
                'dashboard:filter.deleteConfirm.confirmButton',
                'Delete filter'
              ),
              cancelButtonTitle: t(
                i18nLocale,
                'dashboard:filter.deleteConfirm.cancelButton',
                'Keep filter'
              ),
              modalSize: ModalSize.S,
            });
          },
      renameFilter: (newName: string) =>
        commands.updateDashboardFilterParameterName(
          dashboardFilter.id,
          newName
        ),
      validateFilterName: (newName: string) => {
        const result = validateDashboardFilterName(
          newName,
          dashboardFilters
            .filter(filter => filter.id !== dashboardFilter.id)
            .map(filter => filter.name)
        );
        return getValidationErrorMessage(result.error, i18nLocale);
      },
      setShowLinkChartPopover: commands.setShowLinkChartPopover,
      showLinkChartPopover,
      enterLinkMode: () =>
        commands.changeLinkModeDashboardFilterParameter(
          dashboardFilter.id,
          dashboardFilter.type
        ),
    };

    if (dashboardFilterOperations.isUnlinkedDashboardFilter(dashboardFilter)) {
      return {
        ...getUnlinkedDashboardFilterPillProps({
          name: dashboardFilter.name,
          dashboardPillLabels,
          ...settingsButtonProps,
        }),
        id: dashboardFilter.id,
        isLinkedDashboardFilter: false,
      };
    }
    return {
      ...getDashboardFilterPillProps({
        dashboardFilter,
        updateDashboardFilterValues: commands.updateDashboardFilterValues,
        requestDashboardFilterSuggestions:
          commands.requestDashboardFilterSuggestions,
        requestDashboardFilterHierarchicalSuggestions:
          commands.requestDashboardFilterHierarchicalSuggestions,
        filterCachedHierarchicalSuggestions:
          commands.filterCachedHierarchicalSuggestions,
        locale: locale,
        i18nLocale,
        isInReadOnlyMode: false,
        isLoading: isLoading,
        hasInaccessibleWidgets: hasInaccessibleWidgets,
        filterPillUIState: filterPillUI$State[dashboardFilter.id],
        dashboardPillLabels,
        ...settingsButtonProps,
      }),
      id: dashboardFilter.id,
      isLinkedDashboardFilter: true,
    };
  }),
});
